<template>
  <v-dialog :value="value" @input="fechar" max-width="700px" scrollable>
    <v-card v-if="solicitacao">
      <v-card-title class="d-flex justify-space-between">
        <span class="text-h6">Detalhes da Justificativa</span>
        <v-btn icon @click="fechar(false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text>
        <v-row dense>
          <v-col cols="6" sm="3">
            <div class="text-caption text--secondary">ID da Justificativa</div>
            <div class="font-weight-medium">{{ solicitacao.id }}</div>
          </v-col>
          <v-col cols="6" sm="3">
            <div class="text-caption text--secondary">Período da Justificativa</div>
            <div class="font-weight-medium">
              {{
                solicitacao.data_inicial
                  ? `${formatarData(solicitacao.data_inicial)} a ${formatarData(
                      solicitacao.data_final
                    )}`
                  : "---"
              }}
            </div>
          </v-col>
          <v-col cols="6" sm="3">
            <div class="text-caption text--secondary">Data de Criação</div>
            <div class="font-weight-medium">{{ formatarDataHora(solicitacao.created_at) }}</div>
          </v-col>
          <v-col cols="6" sm="3">
            <div class="text-caption text--secondary">Data da Última Alteração</div>
            <div class="font-weight-medium">{{ formatarDataHora(solicitacao.updated_at) }}</div>
          </v-col>
        </v-row>

        <v-divider class="my-3"></v-divider>

        <div class="text-caption text--secondary">Aluno</div>
        <div class="font-weight-medium">
          {{ solicitacao.aluno ? solicitacao.aluno.nomecompleto : "---" }}
        </div>
        <div v-if="solicitacao.matricula && solicitacao.matricula.turma">
          Turma: {{ solicitacao.matricula.turma.descricao }}
        </div>
        <div
          v-if="
            solicitacao.matricula &&
            solicitacao.matricula.turma &&
            solicitacao.matricula.turma.franquia
          "
        >
          Escola: {{ solicitacao.matricula.turma.franquia.descricao }}
        </div>

        <v-divider class="my-3"></v-divider>

        <div class="text-caption text--secondary">Motivo / Observação do Aluno</div>
        <div class="font-weight-medium mb-2">
          {{ tipoLabel(solicitacao.tipo) }}
          <span v-if="solicitacao.observacao"> — {{ solicitacao.observacao }}</span>
        </div>

        <template v-if="solicitacao.anexo">
          <div class="text-caption text--secondary">Anexo</div>
          <v-btn small outlined class="mb-2" @click="imprimirAtestado">
            <v-icon left small>mdi-file-pdf-box</v-icon>Baixar atestado
          </v-btn>
        </template>

        <v-divider class="my-3"></v-divider>

        <div class="text-caption text--secondary mb-1">Status da Justificativa</div>

        <template v-if="solicitacao.status === 'AGUARDANDO'">
          <v-select
            v-model="statusSelecionado"
            :items="statusOptions"
            item-text="label"
            item-value="value"
            label="Status"
            outlined
            dense
            style="max-width: 300px"
          />

          <template v-if="statusSelecionado === 'CONFIRMADO'">
            <div class="periodo-box mb-2">
              <div class="periodo-box-label">Período justificado *:</div>
              <v-row dense>
                <v-col cols="6">
                  <v-menu
                    v-model="menuDataInicial"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    max-width="290px"
                    offset-y
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        :value="formatarData(dataInicial)"
                        label="Data Inicial"
                        append-icon="mdi-calendar"
                        outlined
                        dense
                        readonly
                        hide-details="auto"
                        :error-messages="erroDataInicial"
                        v-bind="attrs"
                        v-on="on"
                      />
                    </template>
                    <v-date-picker
                      v-model="dataInicial"
                      :allowed-dates="naoEhFeriado"
                      @input="menuDataInicial = false"
                    />
                  </v-menu>
                </v-col>
                <v-col cols="6">
                  <v-menu
                    v-model="menuDataFinal"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    max-width="290px"
                    offset-y
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        :value="formatarData(dataFinal)"
                        label="Data Final"
                        append-icon="mdi-calendar"
                        outlined
                        dense
                        readonly
                        hide-details="auto"
                        :error-messages="erroDataFinal"
                        v-bind="attrs"
                        v-on="on"
                      />
                    </template>
                    <v-date-picker
                      v-model="dataFinal"
                      :allowed-dates="naoEhFeriado"
                      @input="menuDataFinal = false"
                    />
                  </v-menu>
                </v-col>
              </v-row>
            </div>
          </template>

          <v-textarea
            v-if="statusSelecionado === 'REJEITADO'"
            v-model="motivoRejeicao"
            label="Observação da Recusa *"
            outlined
            dense
            rows="2"
            counter="200"
            maxlength="200"
          />
        </template>

        <template v-else-if="solicitacao.status === 'CONFIRMADO'">
          <v-chip color="green" text-color="white" label class="mb-2">Confirmado</v-chip>

          <template v-if="!revogando">
            <div>
              <v-btn outlined color="error" @click="revogando = true">
                <v-icon left small>mdi-undo</v-icon>Revogar Aprovação
              </v-btn>
            </div>
          </template>
          <template v-else>
            <v-textarea
              v-model="motivoRevogacao"
              label="Observação da Revogação (obrigatória) *"
              outlined
              dense
              rows="2"
              counter="200"
              maxlength="200"
              class="mt-2"
            />
          </template>
        </template>

        <template v-else>
          <v-chip color="red" text-color="white" label class="mb-2">Rejeitado</v-chip>
          <div v-if="solicitacao.motivo_rejeicao">
            <span class="font-weight-medium">Motivo:</span> {{ solicitacao.motivo_rejeicao }}
          </div>
        </template>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="fechar(false)">
          {{ podeEditar ? "Cancelar" : "Fechar" }}
        </v-btn>
        <v-btn
          v-if="solicitacao.status === 'AGUARDANDO'"
          color="primary"
          :loading="salvando"
          :disabled="statusSelecionado === 'AGUARDANDO'"
          @click="salvarAnalise"
        >
          <v-icon left small>mdi-check</v-icon>Salvar
        </v-btn>
        <v-btn
          v-if="solicitacao.status === 'CONFIRMADO' && revogando"
          color="error"
          :loading="salvando"
          @click="confirmarRevogacao"
        >
          <v-icon left small>mdi-undo</v-icon>Confirmar revogação
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
const TIPOS_LABEL = {
  SAUDE: "Motivos de saúde",
  VIAGEM: "Motivos de viagem",
  ATESTADO_MEDICO: "Atestado médico",
  OUTROS: "Outros",
};

export default {
  props: {
    value: { type: Boolean, default: false },
    justificativaId: { type: [Number, String], default: null },
  },
  data() {
    return {
      solicitacao: null,
      statusSelecionado: "AGUARDANDO",
      dataInicial: null,
      dataFinal: null,
      erroDataInicial: "",
      erroDataFinal: "",
      motivoRejeicao: "",
      revogando: false,
      motivoRevogacao: "",
      feriados: [],
      menuDataInicial: false,
      menuDataFinal: false,
      salvando: false,
      statusOptions: [
        { label: "Aguardando", value: "AGUARDANDO" },
        { label: "Confirmado", value: "CONFIRMADO" },
        { label: "Rejeitado", value: "REJEITADO" },
      ],
    };
  },
  computed: {
    podeEditar() {
      return this.solicitacao && this.solicitacao.status === "AGUARDANDO";
    },
  },
  watch: {
    value(aberto) {
      if (aberto) this.abrir();
    },
  },
  methods: {
    tipoLabel(tipo) {
      return TIPOS_LABEL[tipo] || tipo;
    },
    formatarData(data) {
      if (!data) return "---";
      const [ano, mes, dia] = data.split("-");
      return `${dia}/${mes}/${ano}`;
    },
    formatarDataHora(data) {
      if (!data) return "---";
      return new Date(data)
        .toLocaleString("pt-BR", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
        .replace(",", " às");
    },
    naoEhFeriado(data) {
      return !this.feriados.includes(data);
    },
    async carregarFeriados() {
      try {
        const eventos = await this.$services.calendarioLetivoService.syncAll();
        this.feriados = eventos
          .filter(
            (evento) =>
              evento.evento &&
              (evento.evento.is_feriado ||
                (evento.evento.legenda || "").toLowerCase().includes("feriado"))
          )
          .map((evento) => evento.start);
      } catch (error) {
        this.$handleError(error);
      }
    },
    async abrir() {
      this.revogando = false;
      this.motivoRevogacao = "";
      this.motivoRejeicao = "";
      this.erroDataInicial = "";
      this.erroDataFinal = "";
      try {
        this.solicitacao = await this.$services.justificativaFaltaService.find(
          this.justificativaId
        );
        this.statusSelecionado = this.solicitacao.status;
        this.dataInicial = this.solicitacao.data_inicial;
        this.dataFinal = this.solicitacao.data_final;
        await this.carregarFeriados();
      } catch (error) {
        this.$handleError(error);
      }
    },
    fechar(valor) {
      this.$emit("input", valor === true);
    },
    validarPeriodo() {
      this.erroDataInicial = this.dataInicial ? "" : "O campo Data Inicial é obrigatório";
      this.erroDataFinal = this.dataFinal ? "" : "O campo Data Final é obrigatório";
      return !this.erroDataInicial && !this.erroDataFinal;
    },
    async salvarAnalise() {
      this.salvando = true;
      try {
        if (this.statusSelecionado === "CONFIRMADO") {
          if (!this.validarPeriodo()) {
            this.salvando = false;
            return;
          }
          await this.$services.justificativaFaltaService.aprovar(
            this.solicitacao.id,
            this.dataInicial,
            this.dataFinal
          );
        } else if (this.statusSelecionado === "REJEITADO") {
          if (!this.motivoRejeicao) {
            this.$toast.warn("Informe o motivo da rejeição");
            this.salvando = false;
            return;
          }
          await this.$services.justificativaFaltaService.rejeitar(
            this.solicitacao.id,
            this.motivoRejeicao
          );
        }
        this.$toast.success("Análise registrada com sucesso!");
        this.$emit("salvo");
        this.fechar(false);
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.salvando = false;
      }
    },
    async confirmarRevogacao() {
      if (!this.motivoRevogacao) {
        this.$toast.warn("Informe o motivo da revogação");
        return;
      }
      this.salvando = true;
      try {
        await this.$services.justificativaFaltaService.revogar(
          this.solicitacao.id,
          this.motivoRevogacao
        );
        this.$toast.success("Aprovação revogada com sucesso!");
        this.$emit("salvo");
        this.fechar(false);
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.salvando = false;
      }
    },
    async imprimirAtestado() {
      try {
        await this.$services.justificativaFaltaService.baixarAtestado(this.solicitacao.id);
      } catch (error) {
        this.$handleError(error);
      }
    },
  },
};
</script>

<style scoped>
.periodo-box {
  border: 1px solid #ef9a9a;
  border-radius: 4px;
  padding: 12px;
}
.periodo-box-label {
  font-size: 0.9rem;
  margin-bottom: 8px;
}
</style>
